<template>
  <div class="v-modal">
    <div class="v-modal__header">
      <span v-if="state == 'listLocations'">Locations</span>
      <span v-else-if="state == 'removeLocation'">Remove Location</span>
      <span v-else-if="state == 'listPollutants'">Pollutants</span>
      <span v-else-if="state == 'addPollutant'">Add Pollutant</span>
      <span v-else-if="state == 'removePollutant'">Remove Pollutant</span>
      <div @click="close()" class="close"></div>
    </div>
    <div class="v-modal__body">
      <Errors @dismiss="errors = []" :errors="errors"/>
      <template v-if="state == 'listLocations'">
        <p v-if="scenarioProcesses.length == 1">
          Your business <b>{{ assessment.scenario.name }}</b> has 1 location.
        </p>
        <p v-else>
          Your business <b>{{ assessment.scenario.name }}</b> has {{ scenarioProcesses.length }} locations.
        </p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Location</th>
              <th>Abstraction</th>
              <th>Effluent</th>
              <th>Loss</th>
              <th>Blue WF<br/>(m<sup>3</sup>/yr)</th>
              <th>Grey WF<br/>(m<sup>3</sup>/yr)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scenarioProcess in scenarioProcesses" :key="scenarioProcess.processID">
              <td>{{ scenarioProcess.name || scenarioProcess.processID }}</td>
              <td v-html="getWFString(Math.round(scenarioProcess.abstraction))"></td>
              <td v-html="getWFString(Math.round(scenarioProcess.effluent))"></td>
              <td v-html="getWFString(Math.round(scenarioProcess.loss))"></td>
              <td v-html="getWFString(Math.round(scenarioProcess.blueWF))"></td>
              <td v-html="getWFString(Math.round(scenarioProcess.greyWF))"></td>
              <td>
                <div class="d-flex justify-content-end gap-2">
                  <button @click="setScenarioProcess('list', scenarioProcess)" title="Manage pollutants for this location" class="btn btn-outline-secondary btn-sm"><span class="fa fa-plus"></span> Pollutants</button>
                  <button @click="setScenarioProcess('remove', scenarioProcess)" title="Remove this location" class="btn btn-outline-danger btn-sm"><i class="fa-solid fa-trash"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <p v-else-if="state == 'removeLocation'" class="text-center">
        <br/><br/>
        Are you sure you want to remove location <b>{{ scenarioProcess.name || scenarioProcess.processID }}</b><br/><br/>
        from your business <b>{{ assessment.scenario.name }}</b>?
      </p>
      <template v-else-if="state == 'listPollutants'">
        <p v-if="scenarioProcessSubstances.length == 1">
          Location <b>{{ scenarioProcess.name || scenarioProcess.processID }}</b> has 1 pollutant.
        </p>
        <p v-else>
          Location <b>{{ scenarioProcess.name || scenarioProcess.processID }}</b> has {{ scenarioProcessSubstances.length }} pollutants.
        </p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Pollutant</th>
              <th>Abstraction Concentration</th>
              <th>Effluent Concentration</th>
              <th>Natural Concentration</th>
              <th>Maximum Concentration</th>
              <th>Grey WF<br/>(m<sup>3</sup>/yr)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scenarioProcessSubstance in scenarioProcessSubstances" :key="scenarioProcessSubstance.substanceID">
              <td>{{ scenarioProcessSubstance.substanceID }}</td>
              <td v-html="scenarioProcessSubstance.abstractionConcentration + ' ' + concentrationUnits.find(unit => unit.unitId == scenarioProcessSubstance.unitID).short"></td>
              <td v-html="scenarioProcessSubstance.effluentConcentration + ' ' + concentrationUnits.find(unit => unit.unitId == scenarioProcessSubstance.unitID).short"></td>
              <td v-html="scenarioProcessSubstance.naturalConcentration + ' ' + concentrationUnits.find(unit => unit.unitId == scenarioProcessSubstance.unitID).short"></td>
              <td v-html="scenarioProcessSubstance.maxConcentration + ' ' + concentrationUnits.find(unit => unit.unitId == scenarioProcessSubstance.unitID).short"></td>
              <td v-html="getWFString(Math.round(scenarioProcessSubstance.greyWF))"></td>
              <td><button @click="setScenarioProcessSubstance('remove', scenarioProcessSubstance)" title="Remove this pollutant" class="btn btn-outline-danger btn-sm"><i class="fa-solid fa-trash"></i></button></td>
            </tr>
          </tbody>
        </table>
      </template>
      <template v-else-if="state == 'addPollutant'">
        <p>
          Add a pollutant to location <b>{{ scenarioProcess.name || scenarioProcess.processID }}</b>.
        </p>
        <div class="row mb-4">
          <div class="col-5">
            <label for="substanceId" class="form-label">Pollutant</label>
            <input type="text" v-model="scenarioProcessSubstance.substanceID" id="substanceId" class="form-control"/>
          </div>
          <div class="col-5"></div>
          <div class="col-2">
            <label for="unitId" class="form-label">Unit</label>
            <select v-model="scenarioProcessSubstance.unitID" id="unitId" class="form-select">
              <option v-for="unit of concentrationUnits" :key="unit.unitId" :value="unit.unitId" v-html="unit.short"></option>
            </select>
          </div>
        </div>
        <div class="row mb-3">
          <label for="abstractionConcentration" class="col-3 col-form-label">Abstraction Concentration</label>
          <div class="col-2">
            <input type="text" v-model="scenarioProcessSubstance.abstractionConcentration" id="abstractionConcentration" class="form-control"/>
          </div>
          <div class="col-2"></div>
          <label for="naturalConcentration" class="col-3 col-form-label">Natural Concentration</label>
          <div class="col-2">
            <input type="text" v-model="scenarioProcessSubstance.naturalConcentration" id="naturalConcentration" class="form-control"/>
          </div>
        </div>
        <div class="row mb-2">
          <label for="effluentConcentration" class="col-3 col-form-label">Effluent Concentration</label>
          <div class="col-2">
            <input type="text" v-model="scenarioProcessSubstance.effluentConcentration" id="effluentConcentration" class="form-control"/>
          </div>
          <div class="col-2"></div>
          <label for="maxConcentration" class="col-3 col-form-label">Maximum Concentration</label>
          <div class="col-2">
            <input type="text" v-model="scenarioProcessSubstance.maxConcentration" id="maxConcentration" class="form-control"/>
          </div>
        </div>
      </template>
      <p v-else-if="state == 'removePollutant'" class="text-center">
        <br/><br/>
        Are you sure you want to remove pollutant <b>{{ scenarioProcessSubstance.substanceID }}</b><br/><br/>
        from your location <b>{{ scenarioProcess.name || scenarioProcess.processID }}</b>?
      </p>
      <template v-if="loading">
        <transition name="fade">
          <div class="lottie">
            <Lottie></Lottie>
          </div>
        </transition>
      </template>
    </div>
    <div v-if="state == 'listLocations'" class="v-modal__footer">
      <button @click="close()" class="btn btn-primary">Close</button>
    </div>
    <div v-else-if="state == 'removeLocation'" class="v-modal__footer justify-content-between">
      <button @click="setScenarioProcess('list', null)" class="btn btn-primary"><span class="fa fa-chevron-left"></span> Cancel</button>
      <button @click="deleteScenarioProcess()" class="btn btn-primary">Delete</button>
    </div>
    <div v-else-if="state == 'listPollutants'" class="v-modal__footer justify-content-between">
      <button @click="setScenarioProcess('list', null)" title="Back to locations" class="btn btn-primary"><span class="fa fa-chevron-left"></span></button>
      <button @click="createScenarioProcessSubstance()" class="btn btn-secondary"><span class="fa fa-plus"></span> Add pollutant</button>
    </div>
    <div v-else-if="state == 'addPollutant'" class="v-modal__footer justify-content-between">
      <button @click="setScenarioProcessSubstance('list', null)" class="btn btn-primary"><span class="fa fa-chevron-left"></span> Cancel</button>
      <button @click="saveScenarioProcessSubstance()" class="btn btn-primary">Save</button>
    </div>
    <div v-else-if="state == 'removePollutant'" class="v-modal__footer justify-content-between">
      <button @click="setScenarioProcessSubstance('list', null)" class="btn btn-primary"><span class="fa fa-chevron-left"></span> Cancel</button>
      <button @click="deleteScenarioProcessSubstance()" class="btn btn-primary">Delete</button>
    </div>
  </div>
</template>

<script>
  import {assessment} from "@/services/assessment-model.js";
  import {
    concentrationUnits,
    scenarioProcesses,
    scenarioProcessesLoader,
    scenarioProcessSubstances,
    scenarioProcessSubstancesLoader
  } from "@/services/entity-model.js";
  import { closeModal } from "@/services/modal-model";
  import Errors from "@/components/shared/Errors.vue";
  import Lottie from "@/components/shared/Lottie.vue";
  import { getFunctionGetWFString } from "@/services/data-tools.js";

  export default {
    name: "ScenarioProcess",
    components: {
      Errors,
      Lottie,
    },
    props: {
      modal: {type: Object}
    },
    data() {
      return {
        getWFString: getFunctionGetWFString("yr"),
        concentrationUnits,
        errors: [],
        substate: "list",
        assessment,
        scenarioProcessesLoader,
        scenarioProcesses,
        scenarioProcess: null,
        scenarioProcessSubstancesLoader,
        scenarioProcessSubstances,
        scenarioProcessSubstance: null
      };
    },
    computed: {
      state() {
        if (this.scenarioProcess == null) {
          return "listLocations";
        }
        if (this.scenarioProcessSubstance == null) {
          if (this.substate == "list") {
            return "listPollutants";
          }
          return this.substate + "Location";
        }
        return this.substate + "Pollutant";
      },
      loading() {
        return this.scenarioProcessesLoader.status.loading || this.scenarioProcessSubstancesLoader.status.loading;
      }
    },
    methods: {
      close() {
        this.scenarioProcessSubstances.length = 0;
        closeModal(this.modal);
      },
      setScenarioProcess(substate, scenarioProcess) {
        this.errors = [];
        if ((substate != "list") || (scenarioProcess == null)) {
          this.substate = substate;
          this.scenarioProcess = scenarioProcess;
          if (scenarioProcess == null) {
            this.scenarioProcessSubstances.length = 0;
          }
        } else {
          this.scenarioProcessSubstancesLoader.load(this.assessment.scenario.code, scenarioProcess.processID, scenarioProcessSubstances => {
            this.scenarioProcess = scenarioProcess;
            if (scenarioProcessSubstances.length == 0) {
              this.createScenarioProcessSubstance();
            }
          });
        }
      },
      deleteScenarioProcess() {
        this.validateDelete();
        if (this.errors.length == 0) {
          this.scenarioProcessesLoader.load(this.assessment.scenario.code, this.scenarioProcess.processID, scenarioProcesses => {
            this.setScenarioProcess("list", null);
            this.assessment.loadScenarioDerivedEntities();
          });
        }
      },
      setScenarioProcessSubstance(substate, scenarioProcessSubstance) {
        this.errors = [];
        this.substate = substate;
        this.scenarioProcessSubstance = scenarioProcessSubstance;
        if (scenarioProcessSubstance == null) {
          if (this.scenarioProcessSubstances.length == 0) {
            this.setScenarioProcess("list", null);
          }
        }
      },
      createScenarioProcessSubstance() {
        this.setScenarioProcessSubstance("add", {
          processID: this.scenarioProcess.processID,
          substanceID: null,
          unitID: 9,
          abstractionConcentration: null,
          effluentConcentration: null,
          naturalConcentration: null,
          maxConcentration: null
        });
      },
      saveScenarioProcessSubstance() {
        this.validate();
        if (this.errors.length == 0) {
          this.scenarioProcessSubstancesLoader.load(this.assessment.scenario.code, ...Object.values(this.scenarioProcessSubstance), scenarioProcessSubstances => {
            this.scenarioProcessesLoader.load(this.assessment.scenario.code, scenarioProcesses => {
              this.setScenarioProcessSubstance("list", null);
            });
            assessment.loadScenarioDerivedEntities();
          });
        }
      },
      deleteScenarioProcessSubstance() {
        this.scenarioProcessSubstancesLoader.load(this.assessment.scenario.code, this.scenarioProcessSubstance.processID, this.scenarioProcessSubstance.substanceID, scenarioProcessSubstances => {
          this.scenarioProcessesLoader.load(this.assessment.scenario.code, scenarioProcesses => {
            this.setScenarioProcessSubstance("list", null);
          });
          assessment.loadScenarioDerivedEntities();
        });
      },
      validate() {
        const doubleCheck = a => (a == null) || (a == "");
        const tripleCheck = a => (a == null) || (a == "") || isNaN(a);

        this.errors = [];
        if (doubleCheck(this.scenarioProcessSubstance.substanceID)) {
          this.errors.push("Pollutant must have a name.");
        }
        if (tripleCheck(this.scenarioProcessSubstance.abstractionConcentration)) {
          this.errors.push("Abstraction concentration must be a valid number.");
        }
        if (tripleCheck(this.scenarioProcessSubstance.effluentConcentration)) {
          this.errors.push("Effluent concentration must be a valid number.");
        }
        if (tripleCheck(this.scenarioProcessSubstance.naturalConcentration)) {
          this.errors.push("Natural concentration must be a valid number.");
        }
        if (tripleCheck(this.scenarioProcessSubstance.maxConcentration)) {
          this.errors.push("Maximum concentration must be a valid number.");
        }
        if (parseFloat(this.scenarioProcessSubstance.naturalConcentration) >= parseFloat(this.scenarioProcessSubstance.maxConcentration)) {
          this.errors.push("Maximum concentration must be greater than the natural concentration.");
        }
      },
      validateDelete() {
        this.errors = [];
        if (this.scenarioProcesses.length == 1) {
          this.errors.push("A business must have at least 1 location.");
        }
      }
    }
  }
</script>
